"use client";

import { LoadingIcon } from "@/icons";
import EmailIcon from "@/icons/email";
import UnlockIcon from "@/icons/unlock-icon";
import { BASE_URL, CALLBACK_URL, SIGNUP_API, VALIDATE_SIGNUP_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useModal } from "@/providers/modal-provider";
import { signIn } from "next-auth/react";
import { FormEvent, useState } from "react";
import { toast } from "react-hot-toast";
import OTPInput from "react-otp-input";

type UnlockAfterSignupProps = {
	title: React.ReactNode;
	className?: string;
};

const UnlockAfterSignup = ({ title, className }: UnlockAfterSignupProps) => {
	const [code, setCode] = useState<string>("");
	const [name, setName] = useState<string>("");
	const [email, setEmail] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showVerificationStep, setShowVerificationStep] = useState<boolean>(false);
	const { openAuthModal, closeAuthModal } = useModal();

	const sendOTP = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);

		const formattedEmail = email.toLowerCase();
		const validateEmailResponse = await fetch(VALIDATE_SIGNUP_API, {
			method: "POST",
			body: JSON.stringify({ email: formattedEmail }),
		});

		if (validateEmailResponse.ok) {
			const signupResponse = await signIn("email", {
				email: formattedEmail,
				redirect: false,
			});

			if (signupResponse?.ok) {
				setShowVerificationStep(true);
				toast.success("OTP sent to your email");
			} else {
				toast.error(signupResponse?.error || "Something went wrong");
				console.log("Error occured during sign up", signupResponse?.error);
			}
		} else {
			const { message } = await validateEmailResponse.json();
			toast.error(message);
		}

		setIsLoading(false);
	};

	const handleSignup = async () => {
		if (code.length !== 6) {
			toast.error("Enter a valid OTP");
			return;
		}

		setIsLoading(true);

		const verificationUrl = new URL(`${BASE_URL}/api/auth/callback/email`);
		verificationUrl.searchParams.set("email", email);
		verificationUrl.searchParams.set("token", code);

		if (CALLBACK_URL) {
			verificationUrl.searchParams.set("callbackUrl", CALLBACK_URL);
		}

		const verificationResponse = await fetch(verificationUrl);
		if (verificationResponse.ok) {
			const response = await fetch(SIGNUP_API, {
				method: "POST",
				body: JSON.stringify({ name, email, provider: "Email" }),
			});

			const { message } = await response.json();

			if (response.status === 201) {
				toast.success(message);
				window.location.href = "/";
				return;
			}

			toast.error(message);
		} else {
			toast.error("OTP verification failed, please retry");
		}

		setIsLoading(false);
	};

	const handleSignInClick = () => {
		closeAuthModal();
		openAuthModal();
	};

	return (
		<div className={cn(className)}>
			<UnlockIcon />

			<h2 className="my-6 text-xl font-semibold md:text-2xl">{title ? title : "Sign Up"}</h2>

			{!showVerificationStep ? (
				<form onSubmit={sendOTP}>
					<div className="mb-4">
						<div className="flex items-center rounded-md border border-gray-200 p-2 transition focus-within:border-blue-500">
							<input
								id="name"
								type="name"
								name="name"
								required
								placeholder="Your Name"
								onChange={(e) => setName(e.target.value)}
								className="w-full rounded-md bg-transparent px-2 text-sm outline-none transition-all duration-300 ease-in-out"
							/>
						</div>
					</div>

					<div className="mb-6">
						<div className="flex items-center rounded-md border border-gray-200 p-2 transition focus-within:border-blue-500">
							<span>
								<EmailIcon className="h-5 w-5" />
							</span>
							<input
								id="email"
								type="email"
								name="email"
								required
								placeholder="me@proptechbuzz.com"
								onChange={(e) => setEmail(e.target.value.toLowerCase())}
								className="w-full rounded-md bg-transparent px-2 text-sm lowercase outline-none transition-all duration-300 ease-in-out"
							/>
						</div>
					</div>

					<button
						type="submit"
						disabled={isLoading}
						className={cn(
							"mb-6 flex w-full items-center justify-center gap-3 rounded-md px-8 py-3 text-sm font-semibold uppercase transition",
							isLoading
								? "cursor-not-allowed bg-gray-200 text-gray-500"
								: "bg-brand-primary text-white"
						)}
					>
						{isLoading ? (
							<div>
								<LoadingIcon className="h-4 w-4 animate-spin" />
							</div>
						) : null}
						<span>{isLoading ? "Sending OTP" : "Submit"}</span>
					</button>
				</form>
			) : (
				<>
					<div className="mb-4">
						<label htmlFor="otp" className="mb-2 block text-sm">
							Enter OTP
						</label>

						<OTPInput
							shouldAutoFocus
							value={code}
							onChange={setCode}
							numInputs={6}
							containerStyle="gap-1"
							inputStyle="border rounded-md border-[#B0CAF6] focus:outline-brand-primary text-brand-primary basis-full py-2 text-lg"
							renderInput={(props) => (
								<input {...props} style={{ width: "40px", height: "40px", textAlign: "center" }} />
							)}
						/>
					</div>

					<button
						onClick={handleSignup}
						disabled={isLoading}
						className={cn(
							"mb-6 flex items-center justify-center gap-3 rounded-md px-8 py-2 text-sm font-semibold transition",
							isLoading
								? "cursor-not-allowed bg-gray-200 text-gray-500"
								: "bg-brand-primary text-white"
						)}
					>
						{isLoading ? (
							<div>
								<LoadingIcon className="h-4 w-4 animate-spin" />
							</div>
						) : null}
						<span>{isLoading ? "Verifying" : "Verify"}</span>
					</button>
				</>
			)}

			<h5 className="text-center text-sm font-medium text-light">
				Already have an account?{" "}
				<button type="button" onClick={handleSignInClick} className="font-bold text-normal">
					Sign in
				</button>
			</h5>
		</div>
	);
};

export default UnlockAfterSignup;
